import { ApiErrorResponse } from "@/services/api/client";
import type { IncomingRequest, RequestLesson } from "@/services/api/teacher";
import { useDirectorEnabled } from "@/hooks/useDirectorEnabled";
import { useTalkT } from "./useTalkT";

export type LessonRow = {
  id: string;
  date: string | null;
  time_slot_name: string | null;
  subject_name: string | null;
  klass_name: string | null;
  room_name: string | null;
  assigned_teacher_id?: string | null;
  assigned_teacher_name?: string | null;
  assigned_teacher_short_code?: string | null;
  can_respond?: boolean;
  my_response?: string | null;
  assignment_state?: string | null;
  status?: string | null;
  block_type?: "full_day" | "partial" | null;
  confirmed_at?: string | null;
};

const STATUS_KEY: Record<string, string> = {
  pending: "talk_radar_status_pending",
  accepted: "talk_radar_status_accepted",
  declined: "talk_radar_status_declined",
  cancelled: "talk_radar_status_cancelled",
  booked: "talk_radar_status_booked",
  partially_filled: "talk_radar_status_partially_filled",
  interested: "talk_radar_status_interested",
  filled: "talk_radar_status_filled",
  assigned_elsewhere: "talk_radar_status_assigned_elsewhere",
  expired: "talk_radar_status_expired",
  director_pending: "talk_radar_status_director_pending",
  director_returned: "talk_radar_status_director_returned",
};

export function radarStatusForDirector(status: string, directorOn: boolean): string {
  if (!directorOn && (status === "director_pending" || status === "director_returned")) {
    return "pending";
  }
  return status;
}

export function useRadarStatusLabel() {
  const t = useTalkT();
  const directorOn = useDirectorEnabled();
  return (status: string) => {
    const shown = radarStatusForDirector(status, directorOn);
    if (directorOn && status === "director_pending") {
      return `❄ ${t("talk_radar_status_director_pending")}`;
    }
    const key = STATUS_KEY[shown];
    return key ? t(key) : status;
  };
}

export function lessonActionError(err: unknown, fallback: string): string {
  if (err instanceof ApiErrorResponse) return err.detail || fallback;
  return fallback;
}

export function formatDay(date: string | null | undefined): string {
  const key = (date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return "—";
  const [year, month, day] = key.split("-");
  return `${day}.${month}.${year}`;
}

export function answerLabel(type: string): string {
  if (type === "accepted") return "Zugesagt";
  if (type === "declined") return "Abgelehnt";
  if (type === "pending") return "Offen";
  if (type === "ignored") return "Ignoriert";
  return type;
}

export function teacherNote(
  detail: IncomingRequest & { notes?: string | null }
): string | null {
  const notes = detail.notes?.trim();
  if (notes) return notes;
  const reason = detail.reason?.trim();
  return reason || null;
}

export type LessonChoice = "interested" | "decline";

export function confirmAsk(choice: LessonChoice): string {
  return choice === "interested"
    ? "Diese Lektion als interessiert markieren?"
    : "Diese Lektion ablehnen?";
}

export function lessonStatusWord(
  lesson: LessonRow,
  myTeacherId?: string | null,
  mode: "viewer" | "owner" | "admin" = "viewer",
): string {
  const state = lesson.assignment_state || "";
  const status = lesson.status || "";
  if (state === "director_removed" || status === "director_removed") {
    return "talk_radar_word_removed";
  }
  if (status === "cancelled" || state === "cancelled") return "talk_radar_status_cancelled";
  if (state === "accepted_not_selected") return "talk_radar_word_lost";
  const declined =
    state === "declined" || lesson.my_response === "declined" || status === "declined";
  if (declined) return "talk_radar_status_declined";
  const assignedToOther =
    mode === "viewer" &&
    !!lesson.assigned_teacher_id &&
    lesson.assigned_teacher_id !== myTeacherId;
  if (assignedToOther) return "talk_radar_word_taken";
  if (
    state === "confirmed" ||
    state === "booked" ||
    status === "confirmed" ||
    status === "booked"
  ) {
    return "talk_radar_word_definite";
  }
  if ((state === "accepted" || lesson.my_response === "accepted") && state !== "waiting") {
    return "talk_radar_word_accepted";
  }
  if (state === "waiting" || lesson.my_response === "interested") return "talk_radar_word_waiting";
  return "talk_radar_word_open";
}

export function decidedLabel(lesson: LessonRow): string | null {
  if (
    lesson.my_response === "interested" ||
    lesson.assignment_state === "waiting"
  ) {
    return "Interessiert";
  }
  if (
    lesson.my_response === "declined" ||
    lesson.assignment_state === "declined" ||
    lesson.status === "declined"
  ) {
    return "Abgelehnt";
  }
  return null;
}

export function teacherLessonRows(lessons: RequestLesson[]): LessonRow[] {
  return lessons.map((lesson) => ({
    id: lesson.id,
    date: lesson.date,
    time_slot_name: lesson.time_slot_name,
    subject_name: lesson.subject_name,
    klass_name: lesson.klass_name,
    room_name: lesson.room_name,
    assigned_teacher_id: lesson.assigned_teacher_id,
    assigned_teacher_name: lesson.assigned_teacher_name,
    assigned_teacher_short_code: lesson.assigned_teacher_short_code,
    can_respond: lesson.can_respond,
    my_response: lesson.my_response,
    assignment_state: lesson.assignment_state,
    status: lesson.status,
    block_type: lesson.block_type,
    confirmed_at: lesson.confirmed_at,
  }));
}
